import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { RouterModule } from '@angular/router';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ConfigService } from './shared/services/core/config.service';
import { servicesProviders } from './shared/services/core/providers/servicesProvider';
import { environment } from "src/environments/environment";
import { KeycloakService } from "keycloak-angular";
import { ENVIRONMENT, KeycloakModule } from "@hygie31/keycloak-authentication";
import { interceptorProviders } from "src/app/shared/services/core/providers/interceptorProviders";

/**
 * Factory function to create a TranslateHttpLoader.
 *
 * Used to configure a TranslateHttpLoader for Angular's TranslateService.
 * It sets up the loader to use the provided HttpClient instance for fetching translation files from
 * the specified directory and with the specified file extension.
 *
 * @param {HttpClient} http - The HttpClient instance used for making HTTP requests.
 * @returns {TranslateHttpLoader} A new instance of TranslateHttpLoader configured with the provided HttpClient.
 */
function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/locale/', '.json');
}

/**
 * Factory function to load configuration.
 *
 * Returns an asynchronous function that waits for the
 * `ConfigService` to load its configuration. It's typically used in
 * Angular applications to ensure that configuration settings are
 * loaded before the application initializes.
 *
 * @param {ConfigService} configService - The ConfigService instance responsible for loading the configuration.
 * @returns {Function} An asynchronous function that loads the configuration using the ConfigService.
 */
function configFactory(configService: ConfigService) {
  return async () => {
    await configService.load();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    KeycloakService,
    {provide: ENVIRONMENT, useValue: environment},
    importProvidersFrom(
      BrowserModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      RouterModule.forRoot(routes, {
        // Don't perform initial navigation in iframes or popups
        // initialNavigation: !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled'
        // Set to enabledBlocking to use Angular Universal
      }),
      KeycloakModule
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: configFactory,
      deps: [ConfigService],
      multi: true
    },
    interceptorProviders,
    servicesProviders,
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi())
  ]
};
